import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { useConsent, readConsent, saveConsent } from "@/lib/consent";
import { useLayout, usePages, useTexts } from "@/lib/layout";

const btn = "kicker text-[11px] px-5 py-3 border-2 border-foreground transition-colors";
const solid = `${btn} bg-foreground text-background hover:bg-primary hover:border-primary`;

const PolicyLink = ({ t, onClick }) => {
  const policy = usePages().find((p) => p.key === "cookies");
  if (!policy) return null;
  return <Link to={`/${policy.slug}`} onClick={onClick} className="underline underline-offset-4 hover:text-primary" data-testid="cookie-policy-link">{t.policy_link}</Link>;
};

const Category = ({ title, text, children }) => (
  <div className="flex items-start justify-between gap-6 border-t border-border py-4">
    <div><p className="font-semibold">{title}</p><p className="text-sm text-muted-foreground mt-1 leading-relaxed">{text}</p></div>
    {children}
  </div>
);

const Settings = ({ t, open, setOpen }) => {
  const tracking = useLayout().data?.tracking || {};
  const [choice, setChoice] = useState({ analytics: false, marketing: false });
  useEffect(() => { if (open) setChoice({ analytics: !!readConsent()?.analytics, marketing: !!readConsent()?.marketing }); }, [open]);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg" data-testid="cookie-settings-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{t.title}</DialogTitle>
          <DialogDescription>{t.text}</DialogDescription>
        </DialogHeader>
        <div>
          <Category title={t.necessary_title} text={t.necessary_text}><Switch checked disabled aria-label={t.necessary_title} data-testid="cookie-necessary-switch" /></Category>
          {tracking.uses_analytics && (
            <Category title={t.analytics_title} text={t.analytics_text}><Switch checked={choice.analytics} onCheckedChange={(v) => setChoice((c) => ({ ...c, analytics: v }))} aria-label={t.analytics_title} data-testid="cookie-analytics-switch" /></Category>
          )}
          {tracking.uses_marketing && (
            <Category title={t.marketing_title} text={t.marketing_text}><Switch checked={choice.marketing} onCheckedChange={(v) => setChoice((c) => ({ ...c, marketing: v }))} aria-label={t.marketing_title} data-testid="cookie-marketing-switch" /></Category>
          )}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
          <PolicyLink t={t} onClick={() => setOpen(false)} />
          <button className={solid} onClick={() => { saveConsent(choice); setOpen(false); }} data-testid="cookie-save-btn">{t.save}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const CookieBanner = () => {
  const t = useTexts("cookies_banner");
  const tracking = useLayout().data?.tracking;
  const consent = useConsent();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const on = () => setOpen(true);
    window.addEventListener("en-cookie-settings", on);
    return () => window.removeEventListener("en-cookie-settings", on);
  }, []);
  if (t.title === undefined) return null;
  return (
    <>
      {!consent && !open && (tracking?.uses_analytics || tracking?.uses_marketing) && (
        <div role="dialog" aria-label={t.title} className="fixed z-[70] bottom-4 left-4 right-4 sm:right-auto sm:max-w-md bg-card border-2 border-foreground shadow-[6px_6px_0_0_hsl(var(--foreground)/0.12)] p-5 sm:p-6 animate-in slide-in-from-bottom-4 fade-in duration-500" data-testid="cookie-banner">
          <p className="kicker text-primary text-[10px] flex items-center gap-2"><Cookie className="h-3.5 w-3.5" />Cookies</p>
          <p className="font-display font-bold text-xl mt-2 leading-snug">{t.title}</p>
          <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{t.text} <PolicyLink t={t} /></p>
          <div className="mt-5 grid grid-cols-2 gap-2">
            <button className={solid} onClick={() => saveConsent({ analytics: false, marketing: false })} data-testid="cookie-reject-btn">{t.reject}</button>
            <button className={solid} onClick={() => saveConsent({ analytics: true, marketing: true })} data-testid="cookie-accept-btn">{t.accept}</button>
          </div>
          <button className="mt-3 w-full kicker text-[11px] py-2 hover:text-primary transition-colors underline underline-offset-4" onClick={() => setOpen(true)} data-testid="cookie-configure-btn">{t.configure}</button>
        </div>
      )}
      <Settings t={t} open={open} setOpen={setOpen} />
    </>
  );
};
